import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import * as pdfjsLib from 'pdfjs-dist';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { 
  Upload, FileText, BarChart2, Save, History, Trash2, 
  ChevronRight, AlertCircle, Loader2, Download, Filter, 
  TrendingUp, Users, Award, Layout as LayoutIcon
} from 'lucide-react';
import { 
  collection, addDoc, query, where, getDocs, 
  deleteDoc, doc, serverTimestamp, orderBy 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';
import { isCoachingAdmin as checkIsCoachingAdmin } from '../../utils/rbac';
import toast from 'react-hot-toast';

// Initialize PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

const DataAnalytics = () => {
  const { userProfile, currentUser } = useRealtime();
  const [data, setData] = useState(null);
  const [columns, setExpandedColumns] = useState([]);
  const [numericColumns, setNumericColumns] = useState([]);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [showSaved, setShowSaved] = useState(false);
  const [analysisName, setAnalysisName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [insights, setInsights] = useState(null);
  const [minPercent, setMinPercent] = useState(0);
  const [maxPercent, setMaxPercent] = useState(100);
  const [manualMax, setManualMax] = useState('');

  const isCoachingAdmin = checkIsCoachingAdmin(userProfile, currentUser);
  const coachingId = userProfile?.coachingId || null;

  const parseToNum = (val) => {
    if (typeof val === 'number') return val;
    if (typeof val !== 'string') return null;
    const cleanVal = val.replace(/[%,$]/g, '');
    const num = parseFloat(cleanVal);
    return isNaN(num) ? null : num;
  };

  const filteredAndSortedData = useMemo(() => {
    if (!data || !selectedColumn) return data;
    
    // Determine the base for % calculation: manualMax if provided, otherwise columnMax
    const numericValues = data.map(row => parseToNum(row[selectedColumn])).filter(v => v !== null);
    const columnMax = numericValues.length > 0 ? Math.max(...numericValues) : 100;
    const baseValue = (manualMax && !isNaN(parseFloat(manualMax))) ? parseFloat(manualMax) : columnMax;

    // First, filter the data
    const filtered = data.filter(row => {
      const val = parseToNum(row[selectedColumn]);
      if (val === null) return true; // Keep non-numeric rows if analyzing non-numeric column
      
      // Calculate percentage relative to the baseValue
      const calculatedPercent = baseValue > 0 ? (val / baseValue) * 100 : 0;
      
      // Special case: if the column name itself contains '%' or 'percentage', 
      // or if all values are already <= 100 AND no manual max is set
      const isAlreadyPercentage = (selectedColumn.toLowerCase().includes('%') || 
                                  selectedColumn.toLowerCase().includes('percentage') ||
                                  columnMax <= 100) && !manualMax;

      const effectiveValue = isAlreadyPercentage ? val : calculatedPercent;
      
      return effectiveValue >= minPercent && effectiveValue <= maxPercent;
    });

    // Then, sort the filtered data
    return [...filtered].sort((a, b) => {
      const valA = parseToNum(a[selectedColumn]);
      const valB = parseToNum(b[selectedColumn]);
      
      // Handle numeric sort
      if (valA !== null && valB !== null) {
        return valB - valA; // Descending
      }
      
      // Fallback to string sort for non-numeric columns
      const strA = String(a[selectedColumn] || '').toLowerCase();
      const strB = String(b[selectedColumn] || '').toLowerCase();
      return strB.localeCompare(strA);
    });
  }, [data, selectedColumn, minPercent, maxPercent, manualMax]);

  // Fetch saved analyses
  const fetchSavedAnalyses = useCallback(async () => {
    if (!coachingId) return;
    setLoadingSaved(true);
    try {
      const q = query(
        collection(db, 'coachingAnalyses'),
        where('coachingId', '==', coachingId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const analyses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSavedAnalyses(analyses);
    } catch (error) {
      console.error('Error fetching saved analyses:', error);
    } finally {
      setLoadingSaved(false);
    }
  }, [coachingId]);

  useEffect(() => {
    fetchSavedAnalyses();
  }, [fetchSavedAnalyses]);

  // Handle file parsing
  const parseFile = async (file) => {
    setIsParsing(true);
    const fileName = file.name;
    const extension = fileName.split('.').pop().toLowerCase();

    try {
      let parsedData = [];

      if (extension === 'csv') {
        parsedData = await parseCSV(file);
      } else if (extension === 'xlsx' || extension === 'xls') {
        parsedData = await parseExcel(file);
      } else if (extension === 'pdf') {
        parsedData = await parsePDF(file);
      } else {
        throw new Error('Unsupported file format');
      }

      if (parsedData.length > 0) {
        processParsedData(parsedData);
        toast.success(`Successfully parsed ${fileName}`);
      } else {
        throw new Error('No data found in file');
      }
    } catch (error) {
      console.error('Parsing error:', error);
      toast.error(error.message || 'Failed to parse file');
    } finally {
      setIsParsing(false);
    }
  };

  const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => resolve(results.data),
        error: (error) => reject(error)
      });
    });
  };

  const parseExcel = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Get as array of arrays to find the real header row
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          // Find the first row that has significant data (more than 2 non-empty cells)
          // this helps skip decorative title rows
          const headerRowIndex = rows.findIndex(row => row.filter(cell => cell !== null && cell !== '').length > 2);
          
          if (headerRowIndex === -1) {
            resolve([]);
            return;
          }

          const jsonData = XLSX.utils.sheet_to_json(worksheet, { range: headerRowIndex });
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  };

  const parsePDF = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = '';
    
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n';
    }

    // Very basic PDF table extraction - this is a heuristic approach
    // We try to find rows by splitting by newlines and then splitting by multiple spaces
    const lines = fullText.split('\n').filter(line => line.trim().length > 0);
    if (lines.length < 2) return [];

    const headers = lines[0].split(/\s{2,}/).map(h => h.trim());
    const dataRows = lines.slice(1).map(line => {
      const values = line.split(/\s{2,}/).map(v => v.trim());
      const row = {};
      headers.forEach((header, index) => {
        const val = values[index];
        // Try to convert to number if possible
        row[header] = isNaN(val) ? val : parseFloat(val);
      });
      return row;
    });

    return dataRows;
  };

  const processParsedData = (parsedData) => {
    if (!parsedData || parsedData.length === 0) return;

    // Filter out rows that look like summaries (e.g., "Class Average", "Total")
    const cleanedData = parsedData.filter(row => {
      const firstVal = String(Object.values(row)[0] || '').toLowerCase();
      return !firstVal.includes('average') && !firstVal.includes('total') && !firstVal.includes('summary');
    });

    const allCols = Object.keys(cleanedData[0]);
    
    // Improved numeric detection (handles percentages and strings that look like numbers)
    const isNumeric = (val) => {
      if (typeof val === 'number') return true;
      if (typeof val !== 'string') return false;
      const cleanVal = val.replace(/[%,$]/g, '');
      return !isNaN(parseFloat(cleanVal)) && isFinite(cleanVal);
    };

    const numCols = allCols.filter(col => 
      cleanedData.some(row => isNumeric(row[col]))
    );

    // Try to pick a meaningful default column
    const blacklist = ['sl', 'no', 'id', 'index', 'sn', 's.no', '#'];
    const preferredKeywords = ['total', 'score', 'mark', 'percentage', '%', 'result', 'grade'];
    
    let defaultCol = numCols.find(col => 
      preferredKeywords.some(key => col.toLowerCase().includes(key))
    );

    // If no preferred column found, pick the first one that isn't in the blacklist
    if (!defaultCol) {
      defaultCol = numCols.find(col => 
        !blacklist.some(key => col.toLowerCase().includes(key))
      ) || numCols[0];
    }

    setData(cleanedData);
    setExpandedColumns(allCols);
    setNumericColumns(numCols);
    
    if (defaultCol) {
      setSelectedColumn(defaultCol);
      generateInsights(cleanedData, defaultCol);
    } else {
      setSelectedColumn(allCols[0]);
      generateInsights(cleanedData, allCols[0]);
    }
  };

  const generateInsights = (parsedData, column) => {
    const values = parsedData.map(row => parseToNum(row[column])).filter(v => v !== null);
    
    if (values.length === 0) {
      // Categorical data insights
      const counts = {};
      parsedData.forEach(row => {
        const val = row[column];
        counts[val] = (counts[val] || 0) + 1;
      });
      
      const distribution = Object.entries(counts).map(([name, value]) => ({ name, value }));
      setInsights({
        type: 'categorical',
        distribution,
        total: parsedData.length,
        unique: Object.keys(counts).length
      });
      return;
    }

    const sum = values.reduce((a, b) => a + b, 0);
    const avg = sum / values.length;
    const max = Math.max(...values);
    const min = Math.min(...values);
    const sorted = [...values].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];

    // Distribution for histogram
    const bins = 5;
    const binSize = (max - min) / bins;
    const histogram = Array.from({ length: bins }, (_, i) => {
      const start = min + i * binSize;
      const end = start + binSize;
      const count = values.filter(v => v >= start && (i === bins - 1 ? v <= end : v < end)).length;
      return {
        range: `${start.toFixed(1)}-${end.toFixed(1)}`,
        count
      };
    });

    setInsights({
      type: 'numeric',
      average: avg.toFixed(2),
      max,
      min,
      median,
      total: values.length,
      histogram
    });
  };

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      parseFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/pdf': ['.pdf']
    },
    multiple: false
  });

  const handleSaveAnalysis = async () => {
    if (!analysisName.trim()) {
      toast.error('Please enter a name for the analysis');
      return;
    }

    setIsSaving(true);
    try {
      await addDoc(collection(db, 'coachingAnalyses'), {
        name: analysisName,
        coachingId: userProfile.coachingId,
        createdBy: currentUser.uid,
        data,
        insights,
        selectedColumn,
        createdAt: serverTimestamp()
      });
      toast.success('Analysis saved successfully');
      setAnalysisName('');
      fetchSavedAnalyses();
    } catch (error) {
      console.error('Error saving analysis:', error);
      toast.error('Failed to save analysis');
    } finally {
      setIsSaving(false);
    }
  };

  const loadAnalysis = (analysis) => {
    setData(analysis.data);
    setInsights(analysis.insights);
    setSelectedColumn(analysis.selectedColumn);
    
    const allCols = Object.keys(analysis.data[0]);
    const numCols = allCols.filter(col => 
      analysis.data.some(row => typeof row[col] === 'number')
    );
    
    setExpandedColumns(allCols);
    setNumericColumns(numCols);
    setShowSaved(false);
    toast.success(`Loaded analysis: ${analysis.name}`);
  };

  const deleteAnalysis = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this analysis?')) return;

    try {
      await deleteDoc(doc(db, 'coachingAnalyses', id));
      toast.success('Analysis deleted');
      fetchSavedAnalyses();
    } catch (error) {
      console.error('Error deleting analysis:', error);
      toast.error('Failed to delete analysis');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <TrendingUp className="text-primary-600" />
            Data Analytics & Insights
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Upload student results or datasets to generate visual reports
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSaved(!showSaved)}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
          >
            <History size={18} />
            {showSaved ? 'Hide History' : 'Saved Analyses'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar: Upload and History */}
        <div className="lg:col-span-1 space-y-6">
          {/* Upload Area */}
          <div 
            {...getRootProps()} 
            className={`p-6 border-2 border-dashed rounded-xl transition-all text-center cursor-pointer ${
              isDragActive ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10' : 'border-gray-300 dark:border-gray-700 hover:border-primary-400'
            }`}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center gap-3">
              <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-full text-primary-600">
                {isParsing ? <Loader2 className="animate-spin" /> : <Upload />}
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {isDragActive ? 'Drop file here' : 'Upload Data File'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Supports CSV, Excel, or PDF
                </p>
              </div>
            </div>
          </div>

          {/* History / Saved Section */}
          {showSaved && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <History size={18} className="text-gray-400" />
                  Recent Analyses
                </h3>
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                {loadingSaved ? (
                  <div className="p-8 text-center">
                    <Loader2 className="animate-spin mx-auto text-gray-400" />
                  </div>
                ) : savedAnalyses.length === 0 ? (
                  <div className="p-8 text-center text-gray-500 text-sm">
                    No saved analyses yet
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {savedAnalyses.map((analysis) => (
                      <div 
                        key={analysis.id}
                        onClick={() => loadAnalysis(analysis)}
                        className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors group"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white text-sm">
                              {analysis.name}
                            </p>
                            <p className="text-[10px] text-gray-500 mt-1">
                              {analysis.createdAt?.toDate().toLocaleDateString()} • {analysis.data.length} rows
                            </p>
                          </div>
                          <button 
                            onClick={(e) => deleteAnalysis(e, analysis.id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Column Selector */}
          {data && (
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Filter size={18} className="text-gray-400" />
                Select Analysis Column
              </h3>
              <select 
                value={selectedColumn}
                onChange={(e) => {
                  setSelectedColumn(e.target.value);
                  setManualMax(''); // Reset manual max on column change
                  generateInsights(data, e.target.value);
                }}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none dark:text-white"
              >
                {columns.map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Main Dashboard Area */}
        <div className="lg:col-span-3 space-y-6">
          {!data ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-12 text-center flex flex-col items-center">
              <div className="w-20 h-20 bg-gray-50 dark:bg-gray-700 rounded-full flex items-center justify-center mb-6">
                <LayoutIcon size={40} className="text-gray-300" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Data to Visualize</h2>
              <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                Please upload a CSV, Excel, or PDF file containing your dataset to generate insights and charts.
              </p>
            </div>
          ) : (
            <>
              {/* Top Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {insights?.type === 'numeric' ? (
                  <>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                      <p className="text-sm text-gray-500 mb-1 uppercase tracking-wider font-semibold">Average</p>
                      <h3 className="text-3xl font-bold text-primary-600">{insights.average}</h3>
                      <div className="mt-2 flex items-center text-xs text-green-600 font-medium">
                        <TrendingUp size={12} className="mr-1" />
                        From {insights.total} data points
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                      <p className="text-sm text-gray-500 mb-1 uppercase tracking-wider font-semibold">Highest Value</p>
                      <h3 className="text-3xl font-bold text-green-600">{insights.max}</h3>
                      <div className="mt-2 flex items-center text-xs text-gray-400">
                        <Award size={12} className="mr-1" />
                        Top performance
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                      <p className="text-sm text-gray-500 mb-1 uppercase tracking-wider font-semibold">Lowest Value</p>
                      <h3 className="text-3xl font-bold text-red-600">{insights.min}</h3>
                      <div className="mt-2 flex items-center text-xs text-gray-400">
                        <AlertCircle size={12} className="mr-1" />
                        Bottom performance
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                      <p className="text-sm text-gray-500 mb-1 uppercase tracking-wider font-semibold">Total Records</p>
                      <h3 className="text-3xl font-bold text-primary-600">{insights?.total}</h3>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                      <p className="text-sm text-gray-500 mb-1 uppercase tracking-wider font-semibold">Unique Categories</p>
                      <h3 className="text-3xl font-bold text-green-600">{insights?.unique}</h3>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                      <p className="text-sm text-gray-500 mb-1 uppercase tracking-wider font-semibold">Column</p>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white truncate">{selectedColumn}</h3>
                    </div>
                  </>
                )}
              </div>

              {/* Charts Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Bar Chart / Histogram */}
                <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                    <BarChart2 size={20} className="text-primary-500" />
                    {insights?.type === 'numeric' ? 'Distribution Histogram' : 'Category Distribution'}
                  </h3>
                  <div className="h-80 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={insights?.type === 'numeric' ? insights.histogram : insights.distribution}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                        <XAxis 
                          dataKey={insights?.type === 'numeric' ? 'range' : 'name'} 
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: '#6B7280' }}
                        />
                        <YAxis 
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: '#6B7280' }}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#FFF', borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                        />
                        <Bar 
                          dataKey={insights?.type === 'numeric' ? 'count' : 'value'} 
                          fill="#3B82F6" 
                          radius={[6, 6, 0, 0]} 
                          barSize={insights?.type === 'numeric' ? 60 : 40}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Pie Chart */}
                <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                    <PieChart size={20} className="text-primary-500" />
                    Overall Breakdown
                  </h3>
                  <div className="h-80 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={insights?.type === 'numeric' ? insights.histogram : insights.distribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey={insights?.type === 'numeric' ? 'count' : 'value'}
                          nameKey={insights?.type === 'numeric' ? 'range' : 'name'}
                        >
                          {(insights?.type === 'numeric' ? insights.histogram : insights.distribution).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend verticalAlign="bottom" height={36}/>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Save Section */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col md:flex-row items-center gap-4">
                <div className="flex-1 w-full">
                  <input 
                    type="text"
                    value={analysisName}
                    onChange={(e) => setAnalysisName(e.target.value)}
                    placeholder="Enter analysis name to save..."
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none dark:text-white"
                  />
                </div>
                <button
                  onClick={handleSaveAnalysis}
                  disabled={isSaving || !analysisName.trim()}
                  className="w-full md:w-auto px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold shadow-lg shadow-primary-500/25 transition-all disabled:opacity-50 disabled:shadow-none flex items-center justify-center gap-2"
                >
                  {isSaving ? <Loader2 className="animate-spin" /> : <Save size={20} />}
                  Save Analysis
                </button>
              </div>

              {/* Data Preview Table */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                  <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <FileText size={20} className="text-primary-500" />
                    Live Data Preview
                  </h3>
                  
                  {/* Range Filter */}
                  <div className="flex flex-wrap items-center gap-3 bg-gray-50 dark:bg-gray-700/50 p-2 rounded-xl border border-gray-100 dark:border-gray-600">
                    {/* Max Marks Input */}
                    <div className="flex items-center gap-2 border-r border-gray-200 dark:border-gray-600 pr-3 mr-1">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Max Marks:</span>
                      <input 
                        type="number" 
                        value={manualMax}
                        onChange={(e) => setManualMax(e.target.value)}
                        className="w-16 px-2 py-1 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg outline-none focus:ring-2 focus:ring-primary-500 dark:text-white font-bold text-primary-600"
                        placeholder="Auto"
                      />
                    </div>

                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-2 uppercase tracking-wider">
                      Filter {selectedColumn}:
                    </span>
                    <div className="flex items-center gap-2">
                      <input 
                        type="number" 
                        value={minPercent}
                        onChange={(e) => setMinPercent(Math.max(0, parseInt(e.target.value) || 0))}
                        className="w-16 px-2 py-1 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
                        placeholder="Min %"
                      />
                      <span className="text-gray-400">to</span>
                      <input 
                        type="number" 
                        value={maxPercent}
                        onChange={(e) => setMaxPercent(Math.min(1000, parseInt(e.target.value) || 100))}
                        className="w-16 px-2 py-1 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
                        placeholder="Max %"
                      />
                      <span className="text-sm font-medium text-primary-600 dark:text-primary-400 ml-1">%</span>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto max-h-[400px]">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                      <tr>
                        {columns.map(col => (
                          <th key={col} className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap border-b border-gray-200 dark:border-gray-600">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {filteredAndSortedData.map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30">
                          {columns.map(col => (
                            <td 
                              key={col} 
                              className={`px-4 py-3 text-sm whitespace-nowrap transition-colors ${
                                col === selectedColumn 
                                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium' 
                                  : 'text-gray-600 dark:text-gray-400'
                              }`}
                            >
                              {row[col]?.toString() || '---'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredAndSortedData.length === 0 && (
                    <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                      No students found matching the {minPercent}% to {maxPercent}% range.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataAnalytics;
